import classes from "./CreativeEditingPage.module.css"
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useState, useRef, useEffect, useContext } from "react";
import ProfileMenu from "../../components/NavBar/ProfileMenu";
import ModesSwitch from "../../components/UI/ModesSwitch";
import { useCreative } from "../../hooks/creative/useCreative";
import NavBarPhone from "../../components/NavBarPhone/NavBarPhone";
import CreativeProfileEdit from "./CreativeProfileEdit";
import CreativeProjectEdit from "./CreativeProjectEdit";
import CreativePackageEdit from "./CreativePackageEdit";
import Overview from "./Overview";
import VisualProjectView from "./VisualProjectView";
import ProjectOverview from "./ProjectOverview";
import useBookingNotifications from "../../hooks/useBookingNotifications";
import useUnreadMessagesCount from "../../hooks/messages/useUnreadMessagesCount";
import { NavBarContext } from "../../App";
import { SmallModal } from "../../components/UI/SmallModal/SmallModal";
import { usePublishCreative } from "../../hooks/creative/usePublishCreative";
import { notifySaveError } from '../../util/notifyActionError';

export const CreativeEditingPage = () => {
    const { id: creativeId } = useParams();
    const location = useLocation();
    const [showProfileMenu, setShowProfileMenu] = useState(false);
    const userContainerRef = useRef(null);
    const { setActiveView } = useContext(NavBarContext);
    const { profilePicture, lastName, firstName } = useSelector((state) => state.auth.user);
    const profileMenuRef = useRef(null);
    const navigate = useNavigate();
    const unreadCount = useUnreadMessagesCount();
    const bookingNotifications = useBookingNotifications();
    const hasNotifications = (unreadCount > 0) || (bookingNotifications > 0);
    const { creative, loading, error } = useCreative(creativeId);
    const { handlePublishCreative, loading: publishLoading } = usePublishCreative();
    const [showActivationModal, setShowActivationModal] = useState(false);

    // Use backend's canBePublished field, falling back to manual checks
    const canBePublished = creative?.canBePublished ?? false;
    const hasActivePackages = creative?.packages?.items?.some(p => p.isActive === true) ?? false;
    const hasActiveProjects = creative?.projects?.items?.some(p => p.status === "PUBLISHED") ?? false;
    const finalCanBePublished = canBePublished || (hasActivePackages && hasActiveProjects);

    const handleMessagesClick = () => {
        setActiveView('creator');
        navigate('/messages');
    };

    // Show activation modal once when user returns from creating project/package
    useEffect(() => {
        if (!creative || loading || error) return;

        const savedFromEdit = location.state && location.state.saved;
        const isOverviewRoute = location.pathname.includes(`/creative-editing/${creativeId}/overview`);
        const isPublished = creative.status === "PUBLISHED";

        if (!savedFromEdit || !isOverviewRoute || isPublished || !finalCanBePublished) {
            return;
        }

        const storageKey = `creativeActivationPromptShown_${creative.id}`;
        const alreadyShown = window.localStorage.getItem(storageKey);

        if (!alreadyShown) {
            setShowActivationModal(true);
            window.localStorage.setItem(storageKey, "true");
        }

        // Clean up location state so modal does not re-open on refresh
        navigate(location.pathname, { replace: true, state: {} });
    }, [creative, creativeId, error, finalCanBePublished, loading, location.pathname, location.state, navigate]);

    const handleActivateProfile = async () => {
        if (!creative || publishLoading) return;

        try {
            await handlePublishCreative(creative.id);
            setShowActivationModal(false);
        } catch (e) {
            notifySaveError(e, {
                title: 'Profil konnte nicht aktiviert werden',
                message: 'Fehler beim Aktivieren deines Kreativ-Profils: ' + (e.message || 'Unbekannter Fehler'),
            });
        }
    };

    const handleContinueEditing = () => {
        setShowActivationModal(false);
    };

    // Check if we're in edit mode (profile, project, or package editing)
    const isEditMode = () => {
        const pathname = location.pathname;
        return (
            pathname.includes('/profile') ||
            (pathname.includes('/project') && !pathname.includes('/overview') && !pathname.includes('/view-project')) ||
            pathname.includes('/package')
        );
    };

    const renderSubPage = () => {
        const pathname = location.pathname;

        // Project overview (standalone)
        if (pathname.includes('/project/') && pathname.includes('/overview')) {
            return <ProjectOverview creative={creative} loading={loading} error={error} />;
        }

        // Visual project view
        if (pathname.includes('/view-project/')) {
            return <VisualProjectView creative={creative} loading={loading} error={error} />;
        }

        // Package editing (dedicated editor)
        if (pathname.includes('/package')) {
            return <CreativePackageEdit creative={creative} loading={loading} error={error} />;
        }

        // Project editing (dedicated editor)
        if (pathname.includes('/project')) {
            return <CreativeProjectEdit creative={creative} loading={loading} error={error} />;
        }

        // Profile editing
        if (pathname.includes('/profile')) {
            return <CreativeProfileEdit creative={creative} loading={loading} error={error} />;
        }

        // Default: Overview page (with its own three URL-driven tabs)
        // Handles:
        //   /creative-editing/:id/overview
        //   /creative-editing/:id/arbeiten
        //   /creative-editing/:id/services
        return <Overview creative={creative} loading={loading} error={error} />;
    };

    const isOverviewPage = location.pathname.includes('/project/') && location.pathname.includes('/overview');

    // When visiting /creative-editing/:id without a tab, normalize to /overview
    useEffect(() => {
        const basePath = `/creative-editing/${creativeId}`;
        if (location.pathname === basePath) {
            navigate(`${basePath}/overview`, { replace: true });
        }
    }, [location.pathname, creativeId, navigate]);

    const handleBackClick = (e) => {
        navigate(`/services-studios`);
    };

    const handleProfileMenuClick = () => {
        setShowProfileMenu(prev => !prev);
    };
  
    // If it's the overview page, render it as a standalone page without the CreativeEditingPage wrapper
    if (isOverviewPage) {
        return <ProjectOverview creative={creative} loading={loading} error={error} />;
    }

    // Show skeleton loading state
    if (loading) {
        return (
            <div className={`${classes.creativeEditingPage} ${isEditMode() ? classes.editModeHeight : ''}`}>
                <header className={classes.header}>
                    <Link to="/profile" className={classes.logo}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="52" height="18" viewBox="0 0 52 18" fill="none">
                            <g clipPath="url(#clip0_8485_11701)">
                            <path d="M9.24209 9.11699L7.89716 9.00528C4.56932 8.73889 3.44854 8.39516 3.44854 7.64756C3.44854 6.89996 4.86244 6.48749 7.77646 6.48749C10.8543 6.48749 12.0958 6.89137 12.3544 7.9741L12.3975 8.14596H15.7771L15.7426 7.90536C15.4064 5.05244 12.8372 3.66895 7.89716 3.66895C2.95712 3.66895 0.422446 5.02666 0.422446 7.69053C0.422446 10.131 2.44846 11.3082 7.4316 11.7551L8.63859 11.8668C11.9406 12.1504 13.1648 12.58 13.1648 13.4393C13.1648 14.376 11.6561 14.8572 8.67308 14.8572C5.29351 14.8572 3.56924 14.2299 3.40543 12.9323L3.37957 12.7433H0V12.9753C0.155184 16.0946 2.98299 17.6757 8.41444 17.6757C13.8459 17.6757 16.1995 16.1977 16.1995 13.2847C16.1995 10.6895 14.2511 9.52086 9.24209 9.11699Z" fill="black"/>
                            <path d="M41.2191 4.09892V9.66726C41.2191 12.7866 39.1586 14.72 35.848 14.72C32.7357 14.72 31.5201 13.3365 31.5201 9.77898V4.09892H21.071V0L17.9587 1.30615V13.0444C17.9587 16.284 19.5278 17.6675 23.2091 17.6675C24.4937 17.6675 25.7266 17.4526 26.7698 17.2378L26.9422 17.2034V14.445L26.6835 14.4966C25.33 14.7716 24.7265 14.8747 23.7609 14.8747C21.9763 14.8747 21.071 14.4536 21.071 12.1421V6.67686H29.951L28.313 7.91427V10.7844C28.313 15.4848 30.3131 17.6675 34.5979 17.6675C37.3654 17.6675 39.6414 16.7136 41.2191 14.8919V17.2378H44.4263V4.09892H41.2191Z" fill="black"/>
                            <path d="M48.9173 7.80391C50.0817 7.80391 51.0257 6.90461 51.0257 5.79526C51.0257 4.68592 50.0817 3.78662 48.9173 3.78662C47.7528 3.78662 46.8088 4.68592 46.8088 5.79526C46.8088 6.90461 47.7528 7.80391 48.9173 7.80391Z" fill="#DF315A"/>
                            </g>
                            <defs>
                                <clipPath id="clip0_8485_11701">
                                    <rect width="51.0642" height="17.6761" fill="white"/>
                                </clipPath>
                            </defs>
                        </svg>
                    </Link>
                    <Link to="/" className={classes.navigationLinks}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="63" height="22" viewBox="0 0 63 22" fill="none">
                            <path d="M59.881 10.9998C61.5389 10.9998 62.8828 9.65582 62.8828 7.99799C62.8828 6.34015 61.5389 4.99621 59.881 4.99621C58.2232 4.99621 56.8792 6.34015 56.8792 7.99799C56.8792 9.65582 58.2232 10.9998 59.881 10.9998Z" fill="#DF315A"/>
                            <path d="M11.1762 11.3413L9.54985 11.2047C5.52557 10.879 4.17024 10.4587 4.17024 9.54462C4.17024 8.63052 5.88004 8.12619 9.40389 8.12619C13.1258 8.12619 14.6271 8.62001 14.9399 9.94389L14.992 10.154H19.0788L19.0371 9.85983C18.6305 6.37153 15.5237 4.67992 9.54985 4.67992C3.57598 4.67992 0.510854 6.34001 0.510854 9.59716C0.510854 12.5811 2.96087 14.0206 8.98687 14.5669L10.4464 14.7035C14.4395 15.0503 15.9199 15.5756 15.9199 16.6263C15.9199 17.7715 14.0954 18.3599 10.4882 18.3599C6.40132 18.3599 4.3162 17.5929 4.11811 16.0064L4.08683 15.7752H0V16.0589C0.187661 19.8729 3.60726 21.8062 10.1754 21.8062C16.7435 21.8062 19.5897 19.999 19.5897 16.4372C19.5897 13.2641 17.2335 11.8351 11.1762 11.3413Z" fill="white"/>
                            <path d="M49.8447 5.20516V12.0136C49.8447 15.8277 47.3529 18.1917 43.3495 18.1917C39.5859 18.1917 38.1159 16.5001 38.1159 12.1502V5.20516H25.48V0.193359L21.7164 1.79041V16.1429C21.7164 20.104 23.6138 21.7956 28.0656 21.7956C29.619 21.7956 31.1098 21.5329 32.3713 21.2703L32.5799 21.2282V17.8555L32.2671 17.9185C30.6303 18.2548 29.9005 18.3808 28.7328 18.3808C26.5747 18.3808 25.48 17.866 25.48 15.0396V8.35724H36.2184L34.2375 9.87024V13.3795C34.2375 19.1268 36.6563 21.7956 41.8378 21.7956C45.1844 21.7956 47.9368 20.6293 49.8447 18.4019V21.2703H53.723V5.20516H49.8447Z" fill="white"/>
                        </svg>
                    </Link>
                    <div className={classes.navigaitonHeader}>
                  <Link to={`/creator`} style={{ position: 'relative', display: 'inline-block' }}>
                    <h2 className={`semiboldSmall ${classes.nonactiveState}`}>Buchungen</h2>
                    {bookingNotifications > 0 && (
                      <span style={{
                        position: 'absolute',
                        top: '-2px',
                        right: '-3px',
                        backgroundColor: '#DF315A',
                        borderRadius: '50%',
                        width: '8px',
                        height: '8px',
                        minWidth: '8px'
                      }}>
                      </span>
                    )}
                  </Link>
                  <Link to="/calendar">
                    <h2 className={`semiboldSmall ${classes.nonactiveState}`}>Kalender</h2>
                  </Link>
                  <Link to="/services-studios">
                    <h2 className={`semiboldSmall ${classes.activeState}`}>Studios & Services</h2>
                  </Link>
                  <Link to="/messages" onClick={handleMessagesClick} style={{ position: 'relative', display: 'inline-block' }}>
                    <h2 className={`semiboldSmall ${classes.nonactiveState}`}>Nachrichten</h2>
                    {unreadCount > 0 && (
                      <span style={{
                        position: 'absolute',
                        top: '-4px',
                        right: '-8px',
                        backgroundColor: '#DF315A',
                        color: 'white',
                        borderRadius: '50%',
                        width: '18px',
                        height: '18px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '10px',
                        fontWeight: '600',
                        minWidth: '18px'
                      }}>
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                    </Link>
                    </div>
                    <div className={classes.headerContent}>
                        <ModesSwitch />
                        <div 
                            ref={userContainerRef} 
                            onClick={handleProfileMenuClick} 
                            className={classes.userContainer}
                        >
                            <div className={classes.userImageContainer}>
                                <img src={profilePicture} alt="User Profile" /> 
                            </div>
                        </div>
                    </div>
                </header>
                <div className={classes.skeletonContentContainer}>
                    {/* Left Side - OverviewBox Skeleton */}
                    <div className={classes.skeletonLeftContent}>
                        <button className={classes.backButtonSkeleton} onClick={() => navigate(`/services-studios`)}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="11" height="20" viewBox="0 0 11 20" fill="none">
                                <path fillRule="evenodd" clipRule="evenodd" d="M0 9.6255L8.99855 0.626953L10.2006 1.82903L2.35773 9.67193L10.2486 18.2204L8.99941 19.3734L0.0172915 9.64279L0 9.6255Z" fill="#1F1F1F"/>
                            </svg>
                            <p className="semiboldSmall">Zurück</p>
                        </button>
                        <div className={classes.skeletonOverviewBox}>
                            <div className={classes.skeletonProfileImage}></div>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', width: '100%' }}>
                                <div className={classes.skeletonName}></div>
                                <div className={classes.skeletonJobTitle}></div>
                            </div>
                            <div style={{ width: '100%', height: '0.5px', background: 'rgba(128, 128, 128, 0.50)' }}></div>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', width: '100%' }}>
                                <div className={classes.skeletonText} style={{ width: '120px', height: '16px' }}></div>
                                <div className={classes.skeletonSkillsContainer}>
                                    <div className={classes.skeletonSkill}></div>
                                    <div className={classes.skeletonSkill}></div>
                                    <div className={classes.skeletonSkill}></div>
                                    <div className={classes.skeletonSkill}></div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Right Side - Content Skeleton */}
                    <div className={classes.skeletonRightContent}>
                        <div className={classes.skeletonTabsHeader}>
                            <button className={classes.backButtonSkeletonDisplay} onClick={() => navigate(`/services-studios`)}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="11" height="20" viewBox="0 0 11 20" fill="none">
                                    <path fillRule="evenodd" clipRule="evenodd" d="M0 9.6255L8.99855 0.626953L10.2006 1.82903L2.35773 9.67193L10.2486 18.2204L8.99941 19.3734L0.0172915 9.64279L0 9.6255Z" fill="#1F1F1F"/>
                                </svg>
                                <p className="semiboldSmall">Zurück</p>
                            </button>
                            <div className={classes.skeletonTabs}>
                                <div className={classes.skeletonTab}></div>
                                <div className={classes.skeletonTab}></div>
                                <div className={classes.skeletonTab}></div>
                            </div>
                            <div className={classes.skeletonEditButton}></div>
                        </div>
                        
                        <div className={classes.skeletonContentSection}>
                            <div className={classes.skeletonSectionTitle}></div>
                            <div className={classes.skeletonText}></div>
                            <div className={classes.skeletonText}></div>
                            <div className={classes.skeletonTextShort}></div>
                        </div>
                        
                        <div className={classes.skeletonHorizontalLine}></div>
                        
                        <div className={classes.skeletonContentSection}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                                <div className={classes.skeletonSectionTitle}></div>
                                <div className={classes.skeletonText} style={{ width: '120px', height: '16px' }}></div>
                            </div>
                            <div className={classes.skeletonProjectGrid}>
                                {[...Array(3)].map((_, index) => (
                                    <div key={index} className={classes.skeletonProjectItem}>
                                        <div className={classes.skeletonProjectImage}></div>
                                        <div className={classes.skeletonProjectText}>
                                            <div className={classes.skeletonProjectTitle}></div>
                                            <div className={classes.skeletonProjectAuthor}></div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`${classes.creativeEditingPage} ${isEditMode() ? classes.editModeHeight : ''}`}>
          <header className={classes.header}>
            <Link to="/profile" className={classes.logo}>
                <svg xmlns="http://www.w3.org/2000/svg" width="52" height="18" viewBox="0 0 52 18" fill="none">
                    <g clipPath="url(#clip0_8485_11701)">
                    <path d="M9.24209 9.11699L7.89716 9.00528C4.56932 8.73889 3.44854 8.39516 3.44854 7.64756C3.44854 6.89996 4.86244 6.48749 7.77646 6.48749C10.8543 6.48749 12.0958 6.89137 12.3544 7.9741L12.3975 8.14596H15.7771L15.7426 7.90536C15.4064 5.05244 12.8372 3.66895 7.89716 3.66895C2.95712 3.66895 0.422446 5.02666 0.422446 7.69053C0.422446 10.131 2.44846 11.3082 7.4316 11.7551L8.63859 11.8668C11.9406 12.1504 13.1648 12.58 13.1648 13.4393C13.1648 14.376 11.6561 14.8572 8.67308 14.8572C5.29351 14.8572 3.56924 14.2299 3.40543 12.9323L3.37957 12.7433H0V12.9753C0.155184 16.0946 2.98299 17.6757 8.41444 17.6757C13.8459 17.6757 16.1995 16.1977 16.1995 13.2847C16.1995 10.6895 14.2511 9.52086 9.24209 9.11699Z" fill="black"/>
                    <path d="M41.2191 4.09892V9.66726C41.2191 12.7866 39.1586 14.72 35.848 14.72C32.7357 14.72 31.5201 13.3365 31.5201 9.77898V4.09892H21.071V0L17.9587 1.30615V13.0444C17.9587 16.284 19.5278 17.6675 23.2091 17.6675C24.4937 17.6675 25.7266 17.4526 26.7698 17.2378L26.9422 17.2034V14.445L26.6835 14.4966C25.33 14.7716 24.7265 14.8747 23.7609 14.8747C21.9763 14.8747 21.071 14.4536 21.071 12.1421V6.67686H29.951L28.313 7.91427V10.7844C28.313 15.4848 30.3131 17.6675 34.5979 17.6675C37.3654 17.6675 39.6414 16.7136 41.2191 14.8919V17.2378H44.4263V4.09892H41.2191Z" fill="black"/>
                    <path d="M48.9173 7.80391C50.0817 7.80391 51.0257 6.90461 51.0257 5.79526C51.0257 4.68592 50.0817 3.78662 48.9173 3.78662C47.7528 3.78662 46.8088 4.68592 46.8088 5.79526C46.8088 6.90461 47.7528 7.80391 48.9173 7.80391Z" fill="#DF315A"/>
                    </g>
                    <defs>
                        <clipPath id="clip0_8485_11701">
                            <rect width="51.0642" height="17.6761" fill="white"/>
                        </clipPath>
                    </defs>
                </svg>
            </Link>
            <Link to="/" className={classes.navigationLinks}>
                <svg xmlns="http://www.w3.org/2000/svg" width="63" height="22" viewBox="0 0 63 22" fill="none">
                    <path d="M59.881 10.9998C61.5389 10.9998 62.8828 9.65582 62.8828 7.99799C62.8828 6.34015 61.5389 4.99621 59.881 4.99621C58.2232 4.99621 56.8792 6.34015 56.8792 7.99799C56.8792 9.65582 58.2232 10.9998 59.881 10.9998Z" fill="#DF315A"/>
                    <path d="M11.1762 11.3413L9.54985 11.2047C5.52557 10.879 4.17024 10.4587 4.17024 9.54462C4.17024 8.63052 5.88004 8.12619 9.40389 8.12619C13.1258 8.12619 14.6271 8.62001 14.9399 9.94389L14.992 10.154H19.0788L19.0371 9.85983C18.6305 6.37153 15.5237 4.67992 9.54985 4.67992C3.57598 4.67992 0.510854 6.34001 0.510854 9.59716C0.510854 12.5811 2.96087 14.0206 8.98687 14.5669L10.4464 14.7035C14.4395 15.0503 15.9199 15.5756 15.9199 16.6263C15.9199 17.7715 14.0954 18.3599 10.4882 18.3599C6.40132 18.3599 4.3162 17.5929 4.11811 16.0064L4.08683 15.7752H0V16.0589C0.187661 19.8729 3.60726 21.8062 10.1754 21.8062C16.7435 21.8062 19.5897 19.999 19.5897 16.4372C19.5897 13.2641 17.2335 11.8351 11.1762 11.3413Z" fill="white"/>
                    <path d="M49.8447 5.20516V12.0136C49.8447 15.8277 47.3529 18.1917 43.3495 18.1917C39.5859 18.1917 38.1159 16.5001 38.1159 12.1502V5.20516H25.48V0.193359L21.7164 1.79041V16.1429C21.7164 20.104 23.6138 21.7956 28.0656 21.7956C29.619 21.7956 31.1098 21.5329 32.3713 21.2703L32.5799 21.2282V17.8555L32.2671 17.9185C30.6303 18.2548 29.9005 18.3808 28.7328 18.3808C26.5747 18.3808 25.48 17.866 25.48 15.0396V8.35724H36.2184L34.2375 9.87024V13.3795C34.2375 19.1268 36.6563 21.7956 41.8378 21.7956C45.1844 21.7956 47.9368 20.6293 49.8447 18.4019V21.2703H53.723V5.20516H49.8447Z" fill="white"/>
                </svg>
            </Link>
            <div className={classes.navigaitonHeader}>
                  <Link to={`/creator`} style={{ position: 'relative', display: 'inline-block' }}>
                    <h2 className={`semiboldSmall ${classes.nonactiveState}`}>Buchungen</h2>
                    {bookingNotifications > 0 && (
                      <span style={{
                        position: 'absolute',
                        top: '-2px',
                        right: '-3px',
                        backgroundColor: '#DF315A',
                        borderRadius: '50%',
                        width: '8px',
                        height: '8px',
                        minWidth: '8px'
                      }}>
                      </span>
                    )}
                  </Link>
                  <Link to="/calendar">
                    <h2 className={`semiboldSmall ${classes.nonactiveState}`}>Kalender</h2>
                  </Link>
                  <Link to="/services-studios">
                    <h2 className={`semiboldSmall ${classes.activeState}`}>Studios & Services</h2>
                  </Link>
                  <Link to="/messages" onClick={handleMessagesClick} style={{ position: 'relative', display: 'inline-block' }}>
                    <h2 className={`semiboldSmall ${classes.nonactiveState}`}>Nachrichten</h2>
                    {unreadCount > 0 && (
                      <span style={{
                        position: 'absolute',
                        top: '-4px',
                        right: '-8px',
                        backgroundColor: '#DF315A',
                        color: 'white',
                        borderRadius: '50%',
                        width: '18px',
                        height: '18px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '10px',
                        fontWeight: '600',
                        minWidth: '18px'
                      }}>
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </Link>
            </div>
            <div className={classes.headerContent}>
              <ModesSwitch />
              <div 
                    ref={userContainerRef} 
                    onClick={handleProfileMenuClick} 
                    className={classes.userContainer}
                  >
                  <div className={classes.userImageContainer}>
                    <img src={profilePicture} alt="User Profile" /> 
                  </div>
              </div>
            </div>
          </header>
          <div className={classes.backButton} onClick={handleBackClick}>
            <svg xmlns="http://www.w3.org/2000/svg" width="11" height="20" viewBox="0 0 11 20" fill="none">
              <path fillRule="evenodd" clipRule="evenodd" d="M0 9.6255L8.99855 0.626953L10.2006 1.82903L2.35773 9.67193L10.2486 18.2204L8.99941 19.3734L0.0172915 9.64279L0 9.6255Z" fill="#1F1F1F"/>
            </svg>
          </div>

          {showProfileMenu && (
          <ProfileMenu
            ref={profileMenuRef}
            profilePicture={profilePicture}
            firstName={firstName}
            lastName={lastName}
            setShowProfileMenu={setShowProfileMenu}
            //fromProfile={fromProfile}
            />
          )}
        <div className={classes.mobileNavBar}>
            <NavBarPhone fromServicesStudios={true}/>
        </div>
        <div className={classes.contentContainer}>
            {renderSubPage()}
            {/* {!loading && !error && creative && showActivationModal && ( */}
                <SmallModal
                    open={showActivationModal}
                    onBackdropClick={handleContinueEditing}
                    fromCreateEditService={true}
                >
                    <div className={classes.activateProfileModal}>
                        <button className={classes.closeButtonModal} onClick={handleContinueEditing}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                                <path fillRule="evenodd" clipRule="evenodd" d="M7.50947 8.5L0 0.990535L0.990535 0L8.5 7.50947L16.0095 0L17 0.990535L9.49054 8.5L17 16.0095L16.0095 17L8.5 9.49054L0.990535 17L0 16.0095L7.50947 8.5Z" fill="#1F1F1F"/>
                            </svg>
                        </button>
                        <h2 className="titleSemibold2">Kreativ-Profil aktivieren?</h2>
                        <p className={`${classes.modalDescription} regularLarge`}>
                            Durch die Aktivierung wird dein Kreativ-Profil in deinem Studio angezeigt und ist sofort buchbar.
                            Du kannst es jederzeit wieder aktivieren oder deaktivieren.
                        </p>
                        <div className={classes.activateProfileButtons}>
                            <button
                                className={`${classes.activateProfileButton} mediumLarge`}
                                style={{ backgroundColor: publishLoading ? 'var(--light-grey-2)' : 'var(--stu-rot)', color: publishLoading ? 'var(--dark-grey-3)' : "white" }}
                                onClick={handleActivateProfile}
                                disabled={publishLoading}
                            >
                                {publishLoading ? "Aktivieren..." : "Aktivieren"}
                            </button>
                            <button
                                className={`${classes.activateProfileButton} mediumLarge`}
                                style={{ backgroundColor: 'var(--light-grey-2)' }}
                                onClick={handleContinueEditing}
                            >
                                Weiter bearbeiten
                            </button>
                        </div>
                    </div>
                </SmallModal>
            {/* )} */}
        </div>
        </div>
  );
};

export default CreativeEditingPage;
